import { useMemo } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import { useMedia } from 'react-use';

import { PriceListItem } from '../store/priceListItemsStore';
import { PriceListComposition } from '../api/priceListApi';
import { parseComposition } from './complexBundlerV2/composition';
import { PriceCoefficients } from './complexBundlerV2/coefficients';
import PriceListEditNoClassifier from './PriceListEditNoClassifier';

interface PriceListViewTableProps {
  items: PriceListItem[];
  classifier2Filter: string;
  searchValue: string;
  /** composition прайс-листа — для позиций-наценок (additionalPositions коэффициентов) */
  composition?: string | PriceListComposition;
}

interface GroupedPosition {
  vendorCode: string;
  positionName: string;
  items: PriceListItem[];
  byDiameter: Map<string, PriceListItem>;
  // позиция входит в additionalPositions коэффициентов (composition.priceCoefficients):
  // выделяется цветом и ставится последними строками, как в PriceListEditTableV3
  isAdditional?: boolean;
}

// Стили — те же, что у «Цен комплексов» в PriceListViewComplexTable (единый вид
// с PriceListEditTableV3): fixed-раскладка, sticky-шапка, серые расчётные ячейки
const TABLE_STYLES = `
  .price-list-table { margin-bottom: 0; table-layout: fixed; }
  .price-list-table td, .price-list-table th {
    padding: 0 !important;
    line-height: 1.1;
    vertical-align: middle;
  }
  .price-list-table thead th {
    height: 50px !important;
    vertical-align: middle !important;
    text-align: center !important;
    padding: 0.75rem 0.5rem !important;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #e9ecef;
    font-size: 0.95rem;
  }
  .price-list-table tbody tr:hover td:first-child { background-color: #f1f3f5; }
  .price-list-table td.position-cell {
    padding: 0.4rem 0.6rem !important;
    background-color: #fafbfc;
  }
  /* Ячейки цен только для просмотра — серый фон, как у рассчитанных ячеек комплексов */
  .price-list-table td.calc-cell {
    padding: 0.4rem 0.6rem !important;
    background-color: #f8f9fa;
    text-align: center;
  }
  /* Планшет/телефон (≤991px): матрица не сжимается, а скроллится пальцем внутри
     .table-responsive. Первая колонка «замораживается» (sticky left) — позиция
     всегда видна при горизонтальном скролле по диаметрам.
     border-collapse: separate обязателен: при collapse Chrome не переносит границы
     sticky-ячеек; чтобы сетка не стала двойной, границы пересобраны только снизу/справа. */
  @media (max-width: 991px) {
    .price-list-table { min-width: 640px; border-collapse: separate; border-spacing: 0; }
    .price-list-table th, .price-list-table td { border: 0; border-bottom: 1px solid #dee2e6; }
    .price-list-table th:not(:last-child),
    .price-list-table td:not(:last-child) { border-right: 1px solid #dee2e6; }
    .price-list-table th:first-child,
    .price-list-table td:first-child { position: sticky; left: 0; z-index: 1; }
    .price-list-table thead th:first-child { z-index: 3; }
  }
`;

const matchesClassifier2 = (item: PriceListItem, filter: string): boolean => {
  return !filter || item.classifier2RefValueName === filter || !item.classifier2RefValueName;
};

const matchesSearch = (item: PriceListItem, search: string): boolean => {
  if (!search) return true;
  return (item.positionShortName ?? '').toUpperCase().indexOf(search) > -1
    || (item.positionVendorCode ?? '').toUpperCase().indexOf(search) > -1
    || (item.classifier1RefValueName ?? '').toUpperCase().indexOf(search) > -1
    || (item.classifier2RefValueName ?? '').toUpperCase().indexOf(search) > -1;
};

const sortDiameters = (diameters: string[]): string[] => {
  return [...diameters].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''), 10);
    const numB = parseInt(b.replace(/\D/g, ''), 10);
    return (numA || 0) - (numB || 0) || a.localeCompare(b);
  });
};

// Read-only матрица «позиция × диаметр» для просмотра прайса офиса:
// группировка и порядок строк — как в PriceListEditTableV3, но цена — текст
const PriceListViewTable = (props: PriceListViewTableProps) => {
  const {
    items, classifier2Filter, searchValue, composition,
  } = props;

  // Планшет/телефон: колонка «Позиция» уже (250px не влезает), матрица скроллится
  const isTabMobile = useMedia('(max-width: 991px)');

  const compositionData = parseComposition(composition) as PriceListComposition
    & { priceCoefficients?: PriceCoefficients };
  const coefficients = compositionData.priceCoefficients ?? null;

  const additionalPositionIds = useMemo(
    () => new Set((coefficients?.additionalPositions ?? []).map(p => p.positionId)),
    [coefficients]
  );

  const groupedData = useMemo<GroupedPosition[]>(() => {
    const allGrouped = new Map<string, GroupedPosition>();
    items
      .filter(v => v.classifier1RefValueName && matchesClassifier2(v, classifier2Filter))
      .forEach(item => {
        const key = `${ item.positionVendorCode }__${ item.positionShortName }`;
        let group = allGrouped.get(key);
        if (!group) {
          group = {
            vendorCode: item.positionVendorCode,
            positionName: item.positionShortName,
            items: [],
            byDiameter: new Map(),
          };
          allGrouped.set(key, group);
        }
        group.items.push(item);
        if (item.classifier1RefValueName) group.byDiameter.set(item.classifier1RefValueName, item);
      });

    const groups = Array.from(allGrouped.values())
      .filter(group => group.items.some(i => matchesSearch(i, searchValue)))
      .map((group) => {
        const positionId = group.items.find(item => item.positionId)?.positionId;
        return {
          ...group,
          isAdditional: Boolean(positionId && additionalPositionIds.has(positionId)),
        };
      });
    return [
      ...groups.filter(g => !g.isAdditional),
      ...groups.filter(g => g.isAdditional),
    ];
  }, [items, classifier2Filter, searchValue, additionalPositionIds]);

  const displayedDiameters = useMemo(() => {
    const set = new Set<string>();
    items
      .filter(i => i.classifier1RefValueName && matchesClassifier2(i, classifier2Filter))
      .forEach(i => { if (i.classifier1RefValueName) set.add(i.classifier1RefValueName); });
    return sortDiameters(Array.from(set));
  }, [items, classifier2Filter]);

  const noClassifierItems = useMemo(() => {
    return items.filter(v => !v.classifier1RefValueName && !v.classifier2RefValueName
      && matchesSearch(v, searchValue));
  }, [items, searchValue]);

  return (
    <>
      <style>{ TABLE_STYLES }</style>

      <Row className='mt-4 mb-3 align-items-center'>
        <Col md={ 8 } className='d-flex align-items-baseline flex-wrap'>
          <h5 className='mb-0 font-weight-bold'>Розничные цены</h5>
          <span className='text-muted small ml-3'>
              (разовые услуги)
          </span>
        </Col>
      </Row>

      <Row>
        <Col lg={ 12 }>
          { groupedData.length === 0
            ? <div className='text-muted p-3'>Нет данных для отображения</div>
            : (
              <div className='table-responsive'>
                <Table className='price-list-table table-bordered table-hover'>
                  <thead className='thead-light'>
                    <tr>
                      <th style={ { width: isTabMobile ? '130px' : '250px' } }>Позиция</th>
                      { displayedDiameters.map((diameter) => (
                        <th key={ diameter }>
                          { diameter }
                        </th>
                      )) }
                    </tr>
                  </thead>
                  <tbody>
                    { groupedData.map((group) => (
                      <tr key={ `${ group.vendorCode }__${ group.positionName }` }>
                        <td className={ `font-weight-bold position-cell${ group.isAdditional ? ' text-info' : '' }` }>
                          { group.positionName }
                        </td>
                        { displayedDiameters.map((diameter) => {
                          const item = group.byDiameter.get(diameter);
                          return (
                            <td key={ diameter } className='calc-cell'>
                              { item?.price != null ? item.price : '—' }
                            </td>
                          );
                        }) }
                      </tr>
                    )) }
                  </tbody>
                </Table>
              </div>
            ) }
        </Col>
      </Row>

      { /* Дополнительные позиции (без классификатора) — только просмотр */ }
      { noClassifierItems.length > 0 && (
        <Row className='mt-4'>
          <Col lg={ 12 }>
            <PriceListEditNoClassifier items={ noClassifierItems } readOnly />
          </Col>
        </Row>
      ) }
    </>
  );
};

export default PriceListViewTable;
